import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Label,
  MultiSelect,
  Badge,
  Icon,
  Alert,
  AlertTitle,
  AlertDescription,
  CompactButton,
} from "@ogt/ui";
import type { Stop, LineDetails } from "../../../../types";
import { selectionSummary } from "../../../../utils/selectionSummary";
import { useLineExistence } from "../../../../hooks/useLineExistence";
import { useSelectedLines } from "../../../../hooks/useSelectedLines";
import { useStopLocations } from "../../../../hooks/useStopLocations";
import { useEnrichedStopName } from "src/hooks/useEnrichedStopName";
import { fetchLineDetails } from "../../../../api/lines";
import { isPureNartrafikFromStop } from "../../../../utils/stopType";

interface StopCardProps {
  stop: Stop;
  active: boolean;
  activeStopId?: string | null;
  activeStopLocation?: Stop | null;
  activeStopLocations?: Stop[];
  onRemove: (id: string) => void;
  onSelect: (stop: Stop) => void;
  setActive: (id: string) => void;
  onAddLineById: (lineId: string) => void;
  loading?: boolean;
  onActivateLine?: (lineId: string) => void;
  showStopLocationsButton?: boolean;
  onShowStopLocations?: () => void;
  onHideStopLocations?: () => void;
}

type StopWithCity = Stop & {
  cityName?: string;
  taxeZoneName?: string;
  findPlaceName?: string;
  findTaxeZone?: {
    Id?: number;
    Name?: string;
    TypeAsString?: string | null;
  } | null;
  TaxeZone?: {
    Name?: string;
    name?: string;
  } | null;
  taxeZone?: {
    Name?: string;
    name?: string;
  } | null;
};

const is25xx = (id: string) => /^25\d{2}$/.test(id);
const ALL_LINES_VALUE = "__all_lines__";

const SV_COLLATOR = new Intl.Collator("sv", {
  sensitivity: "base",
  numeric: true,
});

function rootIdOfLocal(id: string | number | null | undefined) {
  return String(id ?? "").split("-")[0];
}

function getExplicitCityName(stop: Stop): string | undefined {
  const s = stop as StopWithCity;

  return (
    s.cityName?.trim() ||
    s.taxeZoneName?.trim() ||
    s.findTaxeZone?.Name?.trim() ||
    s.TaxeZone?.Name?.trim() ||
    s.TaxeZone?.name?.trim() ||
    s.taxeZone?.Name?.trim() ||
    s.taxeZone?.name?.trim() ||
    undefined
  );
}

function getDisplayParts(
  enrichedDisplayName: string,
  stopName: string,
  explicitCityName?: string
): { displayName: string; cityName?: string } {
  const enriched = enrichedDisplayName.trim();
  const fallbackName = stopName.trim();

  if (explicitCityName) {
    const suffix = `, ${explicitCityName}`;

    if (enriched.endsWith(suffix)) {
      return {
        displayName: enriched.slice(0, -suffix.length).trim(),
        cityName: explicitCityName,
      };
    }

    return {
      displayName: enriched || fallbackName,
      cityName: explicitCityName,
    };
  }

  if (enriched.includes(",")) {
    const [namePart, ...cityParts] = enriched.split(",");
    const displayName = namePart.trim();
    const cityName = cityParts.join(",").trim();

    return {
      displayName: displayName || fallbackName || enriched,
      cityName: cityName || undefined,
    };
  }

  return {
    displayName: enriched || fallbackName,
    cityName: undefined,
  };
}

function getStopLocationRawLabel(location?: Stop | null) {
  return String(
    (location as any)?.markerLabel ??
      (location as any)?.type ??
      (location as any)?.StopPointDesignation ??
      (location as any)?.stopPointDesignation ??
      ""
  )
    .replace(/[()]/g, "")
    .trim();
}

function getStopLocationButtonLabel(location: Stop) {
  const raw = getStopLocationRawLabel(location);

  if (raw) return raw.toUpperCase();

  const [, suffix] = String(location.id ?? "").split("-");

  return suffix ? suffix.toUpperCase() : String(location.id);
}

function getStopLocationLabel(
  activeStopId?: string | null,
  activeStopLocation?: Stop | null
) {
  const fromLocation = getStopLocationRawLabel(activeStopLocation);

  if (fromLocation) {
    return `Läge ${fromLocation.toUpperCase()}`;
  }

  const [, suffix] = String(activeStopId ?? "").split("-");

  if (!suffix) return null;

  return `Läge ${suffix.toUpperCase()}`;
}

export function StopCard({
  stop,
  active,
  activeStopId,
  activeStopLocation,
  activeStopLocations = [],
  showStopLocationsButton = false,
  onShowStopLocations,
  onHideStopLocations,
  onRemove,
  onSelect,
  setActive,
  onAddLineById,
  loading = false,
}: StopCardProps) {
  const { expanded, toggleRoot } = useStopLocations();
  const rootId = React.useMemo(() => rootIdOfLocal(stop.id), [stop.id]);
  const { lineIds, setLineIds, removeLine, selectedLines } = useSelectedLines();

  void onRemove;
  void onSelect;
  void onAddLineById;

  React.useEffect(() => {
    if (!active && expanded.has(rootId)) {
      toggleRoot(rootId, false);
    }
  }, [active, expanded, rootId, toggleRoot]);

  const activeLocationBelongsToThisStop = React.useMemo(() => {
    if (!active) return false;
    if (!activeStopLocation) return false;

    return rootIdOfLocal(activeStopLocation.id) === rootId;
  }, [active, activeStopLocation, rootId]);

  const linesSourceStop = React.useMemo(() => {
    return activeLocationBelongsToThisStop && activeStopLocation
      ? activeStopLocation
      : stop;
  }, [activeLocationBelongsToThisStop, activeStopLocation, stop]);

  const labelByLineIdFromSelected = React.useMemo(() => {
    const map = new Map<string, string>();

    for (const line of selectedLines) {
      const id = String(line.id);
      const label = (line as any).LineName ?? id;
      map.set(id, label);
    }

    return map;
  }, [selectedLines]);

  const rawLines: string[] = React.useMemo(
    () => (linesSourceStop.servedLineIds ?? []).map(String),
    [linesSourceStop]
  );

  const candidates = React.useMemo(() => {
    const uniq = new Set<string>();

    for (const id of rawLines) {
      if (!is25xx(id)) {
        uniq.add(id);
      }
    }

    return Array.from(uniq);
  }, [rawLines]);

  const { existing, loading: validating } = useLineExistence(candidates);

  const lines = React.useMemo(
    () => candidates.filter((id) => existing.has(id)),
    [candidates, existing]
  );

  const selected = React.useMemo(
    () => lines.filter((id) => lineIds.includes(id)),
    [lines, lineIds]
  );

  const [lineMeta, setLineMeta] = React.useState<
    Record<string, Pick<LineDetails, "id"> & { LineName?: string }>
  >({});

  const [labelsLoading, setLabelsLoading] = React.useState(false);

  React.useEffect(() => {
    if (!lines.length) return;

    const missing = lines.filter(
      (id) => !labelByLineIdFromSelected.has(id) && !lineMeta[id]
    );

    if (!missing.length) return;

    let alive = true;
    setLabelsLoading(true);

    (async () => {
      try {
        const details = await Promise.all(
          missing.map((id) => fetchLineDetails(id).catch(() => null))
        );

        if (!alive) return;

        setLineMeta((prev) => {
          const next = { ...prev };

          for (const d of details) {
            if (!d) continue;

            const key = String(d.id);

            next[key] = {
              id: d.id,
              LineName: (d as any).LineName,
            };
          }

          return next;
        });
      } finally {
        if (alive) {
          setLabelsLoading(false);
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [lines, labelByLineIdFromSelected, lineMeta]);

  const formatLineLabel = React.useCallback(
    (lid: string) => {
      const fromSelected = labelByLineIdFromSelected.get(lid);

      if (fromSelected) return fromSelected;

      const meta = lineMeta[lid];

      if (meta?.LineName) return meta.LineName;

      return `Linje ${lid}`;
    },
    [labelByLineIdFromSelected, lineMeta]
  );

  const allSelected = lines.length > 0 && selected.length === lines.length;

  const selectValue = React.useMemo(
    () => (allSelected ? [ALL_LINES_VALUE, ...selected] : selected),
    [allSelected, selected]
  );

  const addLineIdsToList = React.useCallback(
    (ids: string[]) => {
      const uniqueIds = Array.from(
        new Set(ids.map((id) => String(id).trim()).filter(Boolean))
      );

      if (!uniqueIds.length) return;

      setLineIds((cur) => {
        const current = new Set(cur.map(String));
        const missing = uniqueIds.filter((id) => !current.has(id));

        if (!missing.length) return cur;

        return [...missing, ...cur];
      });
    },
    [setLineIds]
  );

  const handleChange = React.useCallback(
    (nextRaw: string[]) => {
      const nextUnique = Array.from(new Set(nextRaw.map(String)));

      const hadAllValue = selectValue.includes(ALL_LINES_VALUE);
      const hasAllValue = nextUnique.includes(ALL_LINES_VALUE);

      const toggledAllValue = hadAllValue !== hasAllValue;

      if (toggledAllValue) {
        if (allSelected) {
          for (const id of selected) {
            removeLine(id);
          }

          return;
        }

        const missing = lines.filter((id) => !lineIds.includes(id));
        addLineIdsToList(missing);

        return;
      }

      const cleanNext = nextUnique.filter((id) => id !== ALL_LINES_VALUE);
      const nextSet = new Set(cleanNext);
      const prevSet = new Set(selected);

      const newlyAdded = cleanNext.filter((id) => !prevSet.has(id));

      addLineIdsToList(newlyAdded);

      for (const id of selected) {
        if (!nextSet.has(id)) {
          removeLine(id);
        }
      }
    },
    [
      selectValue,
      allSelected,
      selected,
      lines,
      lineIds,
      addLineIdsToList,
      removeLine,
    ]
  );

  const options = React.useMemo(() => {
    const lineOptions = lines
      .map((lineId) => ({
        value: lineId,
        label: `Linje ${formatLineLabel(lineId)}`,
      }))
      .sort((a, b) => SV_COLLATOR.compare(a.label, b.label));

    return [
      {
        value: ALL_LINES_VALUE,
        label: allSelected ? "Avmarkera alla" : "Välj alla",
      },
      ...lineOptions,
    ];
  }, [lines, formatLineLabel, allSelected]);

  const totalLines = lines.length;
  const selectedCount = selected.length;
  const isLoading = Boolean(loading || validating || labelsLoading);

  const summary = selectionSummary(
    isLoading,
    totalLines,
    selectedCount,
    "linjer",
    "linje"
  );

  const enrichedDisplayName = useEnrichedStopName(stop);
  const explicitCityName = getExplicitCityName(stop);

  const { displayName, cityName } = React.useMemo(
    () =>
      getDisplayParts(
        enrichedDisplayName,
        String(stop.name ?? ""),
        explicitCityName
      ),
    [enrichedDisplayName, stop.name, explicitCityName]
  );

  const activeLocationSuffix = React.useMemo(() => {
    if (!activeLocationBelongsToThisStop) return null;

    const raw = getStopLocationRawLabel(activeStopLocation);

    if (raw) return raw.toUpperCase();

    const [, suffix] = String(activeStopId ?? "").split("-");

    return suffix ? suffix.toUpperCase() : null;
  }, [activeLocationBelongsToThisStop, activeStopId, activeStopLocation]);

  const title = React.useMemo(() => {
    return activeLocationSuffix
      ? `${displayName} (${activeLocationSuffix})`
      : displayName;
  }, [displayName, activeLocationSuffix]);

  const subtitle = cityName;
  const stopLocationButtons = React.useMemo(() => {
    const seen = new Set<string>();

    return activeStopLocations
      .filter((location) => {
        const id = String(location.id);

        if (rootIdOfLocal(id) !== rootId) return false;
        if (seen.has(id)) return false;

        seen.add(id);
        return true;
      })
      .sort((a, b) =>
        getStopLocationButtonLabel(a).localeCompare(
          getStopLocationButtonLabel(b),
          "sv"
        )
      );
  }, [activeStopLocations, rootId]);

  const linesLabelId = `stop-${stop.id}-lines-label`;
  const stopLocationsIsActive = expanded.has(rootId);

  const handleToggleStopLocations = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      if (stopLocationsIsActive) {
        onHideStopLocations?.();
        return;
      }

      onShowStopLocations?.();
    },
    [stopLocationsIsActive, onHideStopLocations, onShowStopLocations]
  );

  return (
    <div className="@container">
      <Card
        className={`
        border-0
        pt-6
        pb-8
        rounded-[var(--box-border-radius)]
        gap-6
        bg-[var(--card-color-bg-interacted)]
      `}
      >
        <CardHeader className="!flex gap-2 !items-start">
          <div className="flex flex-row gap-2 w-full @max-[300px]:!flex-col">
            <Badge variant={isPureNartrafikFromStop(stop) ? "blue" : "red"}>
              <Icon
                size="10"
                name={isPureNartrafikFromStop(stop) ? "n" : "h"}
              />
            </Badge>

            <div className="flex min-w-0 flex-1 flex-col">
              <span className="mb-0 font-medium text-[var(--caption-small-text-size)]">
                {title}
              </span>

              {subtitle && (
                <span className="text-sm opacity-70">{subtitle}</span>
              )}
            </div>
          </div>
          <div className="mb-4 min-w-[80px] ">
            <CompactButton
              type="button"
              size="small"
              variant="secondary"
              showIcon
              iconName="sign"
              activated={stopLocationsIsActive}
              aria-pressed={stopLocationsIsActive}
              onClick={handleToggleStopLocations}
            >
              Lägen
            </CompactButton>
          </div>
        </CardHeader>

        <CardContent>
          {stopLocationButtons.length > 0 && (
            <div className="mt-0 mb-4 flex flex-col gap-2">
              <Label>Hållplatsläge</Label>
              <div className="flex flex-wrap gap-1">
                {stopLocationButtons.map((location) => {
                  const id = String(location.id);
                  const selected = String(activeStopId) === id;
                  const label = getStopLocationButtonLabel(location);

                  return (
                    <CompactButton
                      key={`stop-location-${id}`}
                      type="button"
                      size="small"
                      variant="secondary"
                      className="!text-sm !min-w-[40px]  !min-h-[40px]"
                      activated={selected}
                      aria-pressed={selected}
                      onClick={(event) => {
                        event.stopPropagation();
                        setActive(id);
                      }}
                    >
                      {selected ? `Läge ${label}` : label}
                    </CompactButton>
                  );
                })}
              </div>
            </div>
          )}
          {isLoading ? (
            <div role="status" aria-live="polite" className="w-full">
              <Label className="mb-2">Linjer</Label>

              <div className="flex items-center gap-2">
                <div className="h-11 w-full rounded-md bg-[var(--skeleton-color-bg,#e9eef6)] animate-pulse" />
              </div>

              <span className="sr-only">Laddar linjer…</span>
            </div>
          ) : totalLines > 0 ? (
            <>
              <Label id={linesLabelId} className="mb-2">
                Linjer
              </Label>

              <div className="flex items-center gap-2">
                <MultiSelect
                  options={options}
                  value={selectValue}
                  onValueChange={handleChange}
                  placeholder={summary}
                  ariaLabelledBy={linesLabelId}
                  showCounter={false}
                />
              </div>
            </>
          ) : (
            <Alert variant="warning" dismissible>
              <AlertTitle>Inga linjer hittade.</AlertTitle>
              <AlertDescription className="text-sm mt-2">
                Hållplatsen kan vara trafikerad av Närtrafik.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
